import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Building2, Upload, Image as ImageIcon } from "lucide-react";
import { api, errMsg, logoSrc, local } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { JURISDICTIONS } from "@/lib/format";
import { CropperDialog } from "@/components/CropperDialog";
import { QRCodeCanvas } from "qrcode.react";
import { QrCode } from "lucide-react";

const FHAPAS_BASE = process.env.REACT_APP_FHAPAS_API_URL;

export default function Settings() {
  const { org, setOrg } = useAuth();
  const [form, setForm] = useState({ name: "", brand_color: "#0F766E", logo: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [profile, setProfile] = useState({ address: "", tax_number: "", payment_terms: "", slug: "", locality: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [initialPath, setInitialPath] = useState("");
  const [slugTaken, setSlugTaken] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    local.get("/org-profile/me").then((r) => {
      if (r.data && Object.keys(r.data).length) {
        setProfile((p) => ({ ...p, ...r.data }));
        setInitialPath(`${r.data.slug || ""}/${r.data.locality || ""}`);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const s = profile.slug, l = profile.locality;
    if (!s || !l || `${s}/${l}` === initialPath) { setSlugTaken(false); return; }
    const t = setTimeout(() => {
      local.get(`/brand-available?slug=${s}&locality=${l}`).then((r) => setSlugTaken(!r.data.available)).catch(() => setSlugTaken(false));
    }, 400);
    return () => clearTimeout(t);
  }, [profile.slug, profile.locality, initialPath]);

  const printQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const data = canvas.toDataURL("image/png");
    const url = `${window.location.origin}/${profile.slug}/${profile.locality}`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print the QR poster"); return; }
    w.document.write(`<html><head><title>${(org?.name || "FHAPAS")} — Sign in</title></head><body style="font-family:Arial;text-align:center;padding:56px;color:#0f172a"><h1 style="color:${org?.brand_color || "#0F766E"};margin:0">${(org?.name || "FHAPAS")}</h1><p style="font-size:18px;color:#475569">Scan to sign in to your workspace</p><img src="${data}" style="width:320px;height:320px;margin:16px 0"/><p style="font-family:monospace;font-size:16px">${url}</p><p style="color:#94a3b8">Powered by FHAPAS</p><script>window.onload=function(){setTimeout(function(){window.print()},350)}<\/script></body></html>`);
    w.document.close();
  };

  const saveProfile = async () => {
    if (slugTaken) { toast.error("That branded link is taken — choose a different locality"); return; }
    setSavingProfile(true);
    try {
      await local.post("/org-profile", {
        address: profile.address, tax_number: profile.tax_number, payment_terms: profile.payment_terms,
        slug: profile.slug, locality: profile.locality,
      });
      if (profile.slug && profile.locality) localStorage.setItem("fhapas_brand_path", `${profile.slug}/${profile.locality}`);
      toast.success("Letterhead & branded login saved");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingProfile(false); }
  };

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setCropFile(file);
  };

  const uploadBlob = async (blob) => {
    if (!blob) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "logo.png");
      const { data } = await api.post("/upload", fd);
      const abs = data.url?.startsWith("http") ? data.url : `${FHAPAS_BASE}/api${data.url}`;
      setForm((f) => ({ ...f, logo: abs }));
      setCropFile(null);
      toast.success("Logo uploaded — click Save to apply across FHAPAS");
    } catch (err) { toast.error(errMsg(err, "Upload failed")); }
    finally { setUploading(false); }
  };

  useEffect(() => {
    if (org) setForm({ name: org.name || "", brand_color: org.brand_color || "#0F766E", logo: org.logo || "" });
  }, [org]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/org", { name: form.name, brand_color: form.brand_color, logo: form.logo || null });
      setOrg(data);
      toast.success("Organisation updated");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const changeJurisdiction = async (code) => {
    try {
      await api.post("/jurisdictions", { code });
      const o = (await api.get("/org")).data;
      setOrg(o);
      toast.success("Jurisdiction updated");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="Organisation" subtitle="Branding, currency and jurisdiction" testId="page-settings" />
      <div className="space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700"><Building2 className="h-4 w-4 text-teal-700" /> Profile</div>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Organisation name</Label><Input data-testid="settings-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Brand colour</Label>
                <div className="flex items-center gap-2">
                  <input type="color" data-testid="settings-color" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="h-10 w-14 cursor-pointer rounded-lg border border-slate-200" />
                  <Input value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="font-mono" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Organisation logo</Label>
                <div className="flex items-center gap-3">
                  {form.logo
                    ? <img src={logoSrc(form.logo)} alt="logo" data-testid="settings-logo-preview" onError={(e) => { e.currentTarget.src = "/icon-192.png"; }} className="h-12 w-12 rounded-lg border border-slate-200 bg-white object-contain p-1" />
                    : <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-dashed border-slate-300 text-slate-300"><ImageIcon className="h-5 w-5" /></div>}
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-medium text-teal-700">
                    <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload logo"}
                    <input data-testid="settings-logo-file" type="file" accept="image/*" className="hidden" disabled={uploading} onChange={onLogoFile} />
                  </label>
                </div>
                <Input data-testid="settings-logo" value={form.logo} onChange={(e) => setForm({ ...form, logo: e.target.value })} placeholder="https://… or upload above" />
              </div>
            </div>
            <Button data-testid="settings-save" onClick={save} disabled={saving} className="gap-1.5 bg-teal-700 hover:bg-teal-800"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}</Button>
            <CropperDialog file={cropFile} open={!!cropFile} onOpenChange={(v) => { if (!v) setCropFile(null); }} busy={uploading} onCropped={uploadBlob} />
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 text-sm font-semibold text-slate-700">Letterhead & branded login</div>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Address</Label><Input data-testid="profile-address" value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} placeholder="12 Allen Avenue, Ikeja, Lagos" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Tax number (TIN)</Label><Input data-testid="profile-tax" value={profile.tax_number} onChange={(e) => setProfile({ ...profile, tax_number: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Payment terms</Label><Input data-testid="profile-terms" value={profile.payment_terms} onChange={(e) => setProfile({ ...profile, payment_terms: e.target.value })} placeholder="Net 14 days" /></div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Branded slug</Label><Input data-testid="profile-slug" value={profile.slug} onChange={(e) => setProfile({ ...profile, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} placeholder="stmichael" /></div>
              <div className="space-y-1.5"><Label>Locality</Label><Input data-testid="profile-locality" value={profile.locality} onChange={(e) => setProfile({ ...profile, locality: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} placeholder="allen-avenue" /></div>
            </div>
            {profile.slug && profile.locality && (
              <div className="rounded-lg bg-teal-50 p-3 text-sm text-teal-800" data-testid="branded-url">
                <div>Branded login: <span className="font-mono">{window.location.origin}/{profile.slug}/{profile.locality}</span></div>
                {slugTaken && <div className="mt-1 font-semibold text-rose-600" data-testid="slug-taken">This link is already used by another organisation — choose a different locality.</div>}
                <div ref={qrRef} className="mt-3 flex items-center gap-4">
                  <QRCodeCanvas value={`${window.location.origin}/${profile.slug}/${profile.locality}`} size={96} fgColor="#0F766E" marginSize={2} />
                  <Button type="button" variant="outline" size="sm" data-testid="print-qr" className="gap-1.5 border-teal-200 text-teal-700" onClick={printQR}><QrCode className="h-4 w-4" /> Print QR poster</Button>
                </div>
              </div>
            )}
            <Button data-testid="profile-save" onClick={saveProfile} disabled={savingProfile} className="gap-1.5 bg-teal-700 hover:bg-teal-800"><Save className="h-4 w-4" /> {savingProfile ? "Saving…" : "Save letterhead"}</Button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={org?.jurisdiction || "NG"} onValueChange={changeJurisdiction}>
              <SelectTrigger data-testid="settings-jurisdiction" className="w-64"><SelectValue /></SelectTrigger>
              <SelectContent>
                {JURISDICTIONS.map((j) => <SelectItem key={j.code} value={j.code}>{j.flag} {j.name} ({j.currency})</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-600">Current: {org?.currency || "NGN"}</span>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-3 text-sm font-semibold text-slate-700">Organisation details</div>
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div><dt className="text-slate-400">Type</dt><dd className="font-medium capitalize text-slate-800">{(org?.type || "").replace(/_/g, " ")}</dd></div>
            <div><dt className="text-slate-400">Plan</dt><dd className="font-medium uppercase text-slate-800">{org?.plan || "trial"}</dd></div>
            <div><dt className="text-slate-400">OSIC</dt><dd className="font-mono text-slate-800">{org?.osic || "—"}</dd></div>
            <div><dt className="text-slate-400">Status</dt><dd className="font-medium capitalize text-slate-800">{org?.status || "active"}</dd></div>
          </dl>
        </div>
      </div>
    </div>
  );
}
